import classNames from 'classnames';
import { ReactNode, useEffect } from 'react';

import FormattedMessage from 'components/formatters/FormattedMessage';
import LoadingSpinner from 'components/icons/LoadingSpinner';
import { useModal } from 'components/modal/ModalContext';
import Navigation, { NavigationFooter } from 'components/navigation/Navigation';
import UserLoginForm from 'components/user/UserLoginForm';
import { useIsMobile } from 'helpers/useOs';
import { useAppReady } from 'hooks/useAppReadiness';
import { useAuthUser, useIsAuthenticated } from 'hooks/useAuth';
import DebugPanel, { DebugInfoProvider } from './DebugPanel';
import { LayoutContextProvider } from './LayoutContext';

type LayoutProps = {
  children?: ReactNode;
  bg?: boolean;
  white?: boolean;
  restricted?: boolean;
  fixed?: boolean;
};

export default function Layout(props: LayoutProps) {
  const {
    children,
    bg = false,
    white = false,
    restricted = false,
    fixed = false,
  } = props;
  const isMobile = useIsMobile();

  const user = useAuthUser();
  const isAuthenticated = useIsAuthenticated();
  const authReady = useAppReady();
  const { openModal } = useModal();

  // Handle login modal for restricted routes
  // Note: Routes that need forms should use ModalPage component instead
  useEffect(() => {
    if (!restricted || isAuthenticated || !authReady) return;
    openModal(<UserLoginForm />);
  }, [restricted, isAuthenticated, authReady, openModal]);

  return (
    <LayoutContextProvider>
      <DebugInfoProvider>
        <>
          <div
            className={classNames('layout', {
              white: white,
              fixed: fixed,
              mobile: isMobile,
            })}
          >
            {/* Block rendering until authentication state is initialized.
            {   This prevents components from rendering with inconsistent auth states. */}
            {!authReady ? (
              <div className="centred_content">
                <LoadingSpinner active={true} onDark={!white}>
                  <FormattedMessage id="NAVIGATION.LOADER.APPLICATION" />
                </LoadingSpinner>
              </div>
            ) : (
              <>
                <Navigation white={white} mobile={isMobile} />
                {restricted && !user ? (
                  <div className="centred_content">
                    <FormattedMessage id="NAVIGATION.RESTRICTED" />
                  </div>
                ) : (
                  children
                )}
                <NavigationFooter mobile={isMobile} />
              </>
            )}
          </div>
          <DebugPanel />
        </>
      </DebugInfoProvider>
    </LayoutContextProvider>
  );
}
